/**
 * A retailer's rebate claims and remittance advices, checked against our
 * invoices. Backend: HHNodeServer services/retailerSettlementService.js,
 * utils/retailerSettlementMatch.js. Plan: docs/bunnings-remittance-plan.md.
 * Phase 1: previews only; nothing is saved.
 */

export interface SettlementIssue {
  code: string;
  message: string;
}

export interface SettlementHeadOffice {
  id: number;
  name: string;
  customer_code: string | null;
}

export interface SettlementInvoice {
  id: number;
  invoice_number: string;
  invoice_date: string | null;
  status: string;
  customer_id: number | null;
  customer_name: string | null;
  store_code: string | null;
  total_amount: number;
  balance: number;
}

export interface ClaimRebate {
  name: string;
  rate_pct: number;
  /** 'credit_note' for a payment request, 'bill' for a tax invoice; null when not known. */
  treatment: 'credit_note' | 'bill' | null;
}

export interface ClaimTotalsPart {
  name: string;
  rate_pct: number;
  rebatable_ex_gst: number;
  amount_ex_gst: number;
  gst: number;
  amount_inc_gst: number;
}

export interface ClaimLine {
  invoice_number: string;
  invoice_date: string | null;
  date_processed: string | null;
  store_code: string | null;
  rebatable_ex_gst: number;
  rebate_ex_gst: number;
  share: { amount_ex_gst: number; gst: number; amount_inc_gst: number };
  invoice: SettlementInvoice | null;
  status: 'ok' | 'warn';
  issues: SettlementIssue[];
}

export type ClaimTreatment = 'credit_note' | 'bill' | 'mixed' | null;

export interface ClaimPreviewOk {
  file_name: string;
  ok: true;
  claim_number: string;
  claim_date: string | null;
  claim_status: string | null;
  supplier_code: string | null;
  supplier_name: string | null;
  supplier_credit_note: string | null;
  head_office: SettlementHeadOffice | null;
  treatment: ClaimTreatment;
  rebates: ClaimRebate[];
  totals: {
    parts: ClaimTotalsPart[];
    rebatable_ex_gst: number;
    amount_ex_gst: number;
    gst: number;
    amount_inc_gst: number;
    line_sum_ex_gst: number;
    line_difference: number;
  };
  invoice_dates: { from: string | null; to: string | null };
  issues: SettlementIssue[];
  lines: ClaimLine[];
  counts: { lines: number; matched: number; with_issues: number };
  /** Already recorded under this id. */
  recorded_id: number | null;
  /** Could be recorded as it stands. */
  recordable: boolean;
}

export interface FilePreviewError {
  file_name: string;
  ok: false;
  error: string;
  recordable?: false;
}

export type ClaimPreview = ClaimPreviewOk | FilePreviewError;

export interface ClaimRateCheck {
  head_office: SettlementHeadOffice;
  claimed_pct: number;
  agreed_pct: number | null;
  agreements: { id: number; name: string; settlement: string; rate_total: number }[];
  matches: boolean;
  note: string | null;
}

export interface ClaimsPreview {
  claims: ClaimPreview[];
  rates: ClaimRateCheck[];
}

export interface RemittanceLine {
  row: number;
  date: string | null;
  type: string;
  kind: 'invoice' | 'credit_claim' | 'rebate_claim' | 'other';
  document_number: string;
  store_name: string | null;
  store_code: string | null;
  value: number | null;
  discount: number;
  amount_paid: number;
  invoice: SettlementInvoice | null;
  credit_note: { id: number; credit_note_number: string } | null;
  rebate: {
    claims: { claim_number: string; claim_id: number | null; amount_ex_gst: number; gst: number; amount_inc_gst: number; is_remainder: boolean }[];
    amount_inc_gst: number;
  } | null;
  /** What recording would allocate to the invoice. */
  allocation: number;
  /** 'short' can be recorded, and leaves the invoice part paid. */
  status: 'ok' | 'short' | 'held';
  issues: SettlementIssue[];
}

export interface RemittanceClaimUse {
  claim_id: number | null;
  claim_number: string;
  claim_total_inc_gst: number;
  settled_before_inc_gst: number;
  taken_here_inc_gst: number;
  deducted_as_line: boolean;
  invoices_here: number | null;
  remainder_here: boolean;
}

export interface RemittancePreviewOk {
  file_name: string;
  ok: true;
  creditor_code: string | null;
  payment_number: string | null;
  payment_date: string | null;
  head_office: SettlementHeadOffice | null;
  already_recorded: { id: number; payment_date: string } | null;
  blocks: SettlementIssue[];
  notes: SettlementIssue[];
  lines: RemittanceLine[];
  claims: RemittanceClaimUse[];
  totals: {
    remittance_total: number | null;
    lines_paid: number;
    invoices_paid: number;
    other_lines: number;
    allocated: number;
    rebate_inc_gst: number;
    deposit: number;
    held_count: number;
    held_amount: number;
    short_count: number;
  };
  ready: boolean;
}

export type RemittancePreview = RemittancePreviewOk | FilePreviewError;

export interface SettlementFile {
  file_name: string;
  text: string;
}

export const CLAIM_TREATMENT_LABELS: Record<'credit_note' | 'bill' | 'mixed', string> = {
  credit_note: 'Credit note (Bunnings sends a payment request)',
  bill: 'Bill from Bunnings (Bunnings sends a tax invoice)',
  mixed: 'Mixed: a rebate and services in one claim',
};

export interface RecordedClaim {
  id: number;
  head_office_id: number;
  head_office_name: string;
  claim_number: string;
  claim_date: string | null;
  claim_status: string | null;
  supplier_code: string | null;
  /** The first day of the month the claim is for. */
  period_month: string;
  treatment: 'credit_note' | 'bill';
  parts: ClaimTotalsPart[];
  rebatable_ex_gst: number;
  amount_ex_gst: number;
  gst: number;
  amount_inc_gst: number;
  our_reference: string | null;
  file_name: string | null;
  status: 'recorded' | 'void';
  void_reason: string | null;
  line_count: number;
  matched_count: number;
  /** Taken off recorded remittances so far. */
  settled_inc_gst: number;
  remaining_inc_gst: number;
  /** Null: not for Xero (recorded before posting was switched on, or it is off). */
  xero_sync_status: XeroSyncStatus | null;
  xero_sync_error: string | null;
  /** The credit note's or bill's number in Xero. */
  xero_doc_number: string | null;
}

export interface RecordedClaimLine {
  id: number;
  invoice_number: string;
  invoice_id: number | null;
  our_invoice_number: string | null;
  invoice_date: string | null;
  store_code: string | null;
  rebatable_ex_gst: number;
  rebate_ex_gst: number;
  share_inc_gst: number;
  settled_inc_gst: number;
  /** Payment numbers it was settled on. */
  settled_on: string | null;
}

export interface RecordedClaimDetail extends RecordedClaim {
  lines: RecordedClaimLine[];
}

export interface RecordedRemittance {
  id: number;
  head_office_id: number;
  head_office_name: string;
  payment_number: string;
  payment_date: string;
  creditor_code: string | null;
  file_name: string | null;
  lines_paid: number;
  invoices_paid: number;
  other_lines: number;
  rebate_inc_gst: number;
  deposit: number;
  payment_id: number | null;
  status: 'recorded' | 'void';
  void_reason: string | null;
  line_count: number;
  short_count: number;
  xero_sync_status: XeroSyncStatus | null;
  xero_sync_error: string | null;
  /** Something it made in Xero is still there. */
  in_xero: boolean;
}

export interface RecordedRemittanceLine {
  id: number;
  row_number: number | null;
  kind: RemittanceLine['kind'];
  line_type: string | null;
  document_number: string | null;
  line_date: string | null;
  store_code: string | null;
  store_name: string | null;
  amount_paid: number;
  allocated: number;
  invoice_id: number | null;
  invoice_number: string | null;
  credit_note_id: number | null;
  credit_note_number: string | null;
  rebate_inc_gst: number;
  short: boolean;
}

export interface RecordedRemittanceDetail extends RecordedRemittance {
  lines: RecordedRemittanceLine[];
  claims: { claim_id: number; claim_number: string; treatment: 'credit_note' | 'bill'; invoices: number; amount_inc_gst: number; remainder: boolean }[];
}

export interface RecordClaimsResult {
  recorded: number;
  claims: RecordedClaim[];
}

export type XeroSyncStatus = 'pending' | 'pushing' | 'synced' | 'failed';

/** Voiding a remittance in Xero leaves what its API cannot remove to a person. */
export interface VoidedRemittance extends RecordedRemittanceDetail {
  xero_to_do: string[];
}

/** The Bunnings remittances section of Accounts → Xero. */
export interface RemittanceXeroSettings {
  enabled_here: boolean;
  connected: boolean;
  /** The connection may write payments and bank transfers. */
  can_post: boolean;
  missing_scopes: string[];
  enabled: boolean;
  clearing_account_code: string | null;
  bank_account_code: string | null;
  services_account_code: string | null;
  input_tax_type: string;
  rebate_account_code: string | null;
  gst_tax_type: string | null;
  /** What is missing before anything can go, in words. */
  problems: string[];
  queue: { claims_pending: number; claims_failed: number; remittances_pending: number; remittances_failed: number };
}

export interface RemittanceXeroLists {
  bank_accounts: { code: string; name: string; type: string }[];
  expense_accounts: { code: string; name: string; type: string }[];
  tax_rates: { tax_type: string; name: string; rate: number }[];
}

export interface SaveRemittanceXeroSettings {
  enabled?: boolean;
  clearing_account_code?: string | null;
  bank_account_code?: string | null;
  services_account_code?: string | null;
  input_tax_type?: string | null;
}
